import React, { useState } from "react";
import { Button, Drawer, Popover, Select, Tag } from "antd";
import {
  ArrowLeft,
  Printer,
  Download,
  RotateCcw,
  BarChart3,
  SlidersHorizontal,
  Store,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useReportStore } from "../../Page/reports/ReportStoreContext";
import { drawerTitle } from "../AppUi";

interface ReportPageProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  backPath: string;
  stats?: Array<{ title: string; value: string; colorClass?: string }>;
  onExportCSV?: () => void;
  isExportDisabled?: boolean;
  onRefresh?: () => void;
  filterControls?: React.ReactNode;
  isCustomRender?: boolean;
  children?: React.ReactNode;
}

export const ReportPage: React.FC<ReportPageProps> = ({
  title,
  description,
  icon,
  backPath,
  stats,
  onExportCSV,
  isExportDisabled = false,
  onRefresh,
  filterControls,
  isCustomRender = false,
  children,
}) => {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { stores, isLoadingStores, selectedStoreIds, setSelectedStoreIds } =
    useReportStore();

  const storeOptions = stores.map((store) => ({
    value: store.id,
    label: store.name,
  }));

  const storeFilterEmpty = selectedStoreIds.length === 0;

  return (
    <div className="p-6 min-h-screen bg-gray-50 flex flex-col gap-6 printable-report">
      {/* Detail Header Control Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 pb-4 no-print">
        <div className="flex items-center gap-3.5">
          <Button
            type="text"
            icon={<ArrowLeft className="w-5 h-5 text-gray-600" />}
            onClick={() => navigate(-1)}
            className="flex items-center justify-center p-1.5 hover:bg-gray-150 rounded-lg no-print"
          />
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-150">
              {icon}
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-gray-900 leading-tight">
                  {title}
                </h1>
                {!isCustomRender && stats && stats.length > 0 && (
                  <Popover
                    placement="bottom"
                    trigger="hover"
                    content={
                      <div className="flex flex-col gap-2.5 min-w-[230px]">
                        {stats.map((stat, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between gap-6 font-sans"
                          >
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                              {stat.title}
                            </span>
                            <span
                              className={`text-sm font-black ${stat.colorClass || "text-gray-900"}`}
                            >
                              {stat.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <Button
                      shape="circle"
                      size="small"
                      icon={
                        <BarChart3 className="w-3.5 h-3.5 text-indigo-600" />
                      }
                      className="flex items-center justify-center border-gray-200 no-print"
                    />
                  </Popover>
                )}
              </div>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
        </div>

        {/* Global actions */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          {!isCustomRender && (
            <Button
              icon={<SlidersHorizontal className="w-4 h-4" />}
              className="text-xs font-semibold hover:border-gray-300 rounded-lg"
              onClick={() => setIsFilterOpen(true)}
            >
              Filters
              {selectedStoreIds.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                  {selectedStoreIds.length}
                </span>
              )}
            </Button>
          )}
          <Button
            icon={<Printer className="w-4 h-4" />}
            className="text-xs font-semibold hover:border-gray-300 rounded-lg"
            onClick={() => window.print()}
            disabled
          >
            Print
          </Button>
          {onExportCSV && (
            <Button
              type="primary"
              icon={<Download className="w-4 h-4" />}
              className="bg-indigo-600 border-none text-xs hover:bg-indigo-700 font-semibold rounded-lg"
              onClick={onExportCSV}
              disabled
            >
              Export PDF
            </Button>
          )}
          {onRefresh && (
            <Button
              shape="circle"
              icon={<RotateCcw className="w-4 h-4 text-gray-500" />}
              onClick={onRefresh}
              className="hover:border-gray-300"
            />
          )}
        </div>
      </div>

      {/* Print view header */}
      {!isCustomRender && (
        <div className="hidden print:block text-center border-b pb-4 mb-4">
          <h1 className="text-2xl font-black text-gray-955 uppercase leading-tight">
            {title}
          </h1>
          <p className="text-xs text-gray-600 font-medium mt-1">
            Generated: {dayjs().format("YYYY-MM-DD HH:mm")}
          </p>
        </div>
      )}

      {/* Interactive Filter Drawer */}
      {!isCustomRender && (
        <Drawer
          title={drawerTitle("Filters")}
          placement="right"
          width={380}
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          className="no-print"
        >
          <div className="flex flex-col gap-4 font-sans">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                <Store className="w-3.5 h-3.5" />
                Stores
                <Tag color="red" className="ml-auto font-bold text-[10px]">
                  Required
                </Tag>
              </div>
              <Select
                mode="multiple"
                loading={isLoadingStores}
                placeholder="Select at least one store"
                options={storeOptions}
                value={selectedStoreIds}
                onChange={setSelectedStoreIds}
                className="w-full"
                maxTagCount="responsive"
              />
              {storeFilterEmpty ? (
                <span className="text-[10px] text-rose-500 font-medium">
                  Select at least one store- reports are always filtered by
                  store.
                </span>
              ) : (
                <span className="text-[10px] text-gray-400">
                  <Tag color="indigo" className="mr-1">
                    {selectedStoreIds.length}
                  </Tag>
                  store(s) selected- data is restricted to these stores.
                </span>
              )}
            </div>
            {filterControls}
          </div>
        </Drawer>
      )}

      {/* Main Content */}
      {!isCustomRender && storeFilterEmpty ? (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-12 flex flex-col items-center gap-3 no-print">
          <div className="p-3 rounded-full bg-indigo-50">
            <Store className="w-8 h-8 text-indigo-600" />
          </div>
          <p className="text-sm font-bold text-gray-800">
            Store filter is required
          </p>
          <p className="text-xs text-gray-400 text-center max-w-sm">
            Select at least one store from the Filters section to view this
            report. Data is always restricted to the selected store(s).
          </p>
          <Button
            type="primary"
            className="bg-indigo-600 border-none text-xs hover:bg-indigo-700 font-semibold rounded-lg"
            onClick={() => setIsFilterOpen(true)}
          >
            Open Filters
          </Button>
        </div>
      ) : (
        children
      )}

      {/* Printing specific styles to append */}
      <style>{`
        @media print {
          body {
            background: #fff !important;
            color: #000 !important;
          }
          aside, nav, header, .sidebar, .topbar, .navbar, .no-print, .ant-btn {
            display: none !important;
          }
          .printable-report {
            padding: 0 !important;
            min-height: auto !important;
            background: transparent !important;
          }
          ${
            isCustomRender
              ? `
            .receipt-container {
              padding: 0 !important;
              margin: 0 !important;
              display: flex !important;
              justify-content: center !important;
              align-items: flex-start !important;
              width: 100% !important;
            }
            .receipt-container > div {
              box-shadow: none !important;
              border: none !important;
              width: 100% !important;
              max-width: 100% !important;
              padding: 0 !important;
              margin: 0 auto !important;
            }
          `
              : `
            .custom-report-table {
              border: none !important;
            }
            .ant-table-container {
              border: 1px solid #ddd !important;
            }
            .ant-table-thead > tr > th {
              background-color: #f5f5f5 !important;
              color: #000 !important;
              border-bottom: 2px solid #ddd !important;
            }
            .ant-table-cell {
              padding: 8px 12px !important;
              border-bottom: 1px solid #ddd !important;
              font-size: 11px !important;
            }
          `
          }
        }
      `}</style>
    </div>
  );
};
